import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { ControlPlaneAgentRecord } from '@/lib/types/control-plane-agents';
import type { User } from '@/lib/api/generated/control-api.schemas';
import { TenantAppUserRolePicker } from '@/features/identity/tenant-app-user-role-picker';
import {
  JsonPreview,
  SelectField,
  TextAreaField,
  TextField,
  ToggleField,
} from './automation-form-fields';
import { type LightweightAutomationRecord } from './automation-domain';
import {
  LIGHTWEIGHT_AUTOMATION_TEMPLATE_VARIABLES,
  apiBatchBodyShapeOptions,
  buildDeliveryPreview,
  createLightweightAutomationFormSchema,
  createLightweightAutomationFormValues,
  digestDestinationOptions,
  executionClassOptions,
  executionModeOptions,
  flattenErrors,
  httpMethodOptions,
  inputSourceKindOptions,
  iterationKindOptions,
  ownerModeOptions,
  outputModeOptions,
  scheduleKindOptions,
  statusOptions,
  toFormValues,
  toRecord,
  type LightweightAutomationFormValues,
  webhookBodyShapeOptions,
} from './automation-form-model';

export {
  createLightweightAutomationFormSchema,
  createLightweightAutomationFormValues,
  toFormValues,
  type LightweightAutomationFormValues,
};

export interface AutomationFormProps {
  tenantId: string;
  title: string;
  submitLabel: string;
  agents: ControlPlaneAgentRecord[];
  tenantUsers: User[];
  currentUserId: string;
  canAuthorShared: boolean;
  initialValues?: LightweightAutomationFormValues;
  onSubmit: (record: LightweightAutomationRecord) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
}

export function AutomationForm({
  tenantId,
  title,
  submitLabel,
  agents,
  tenantUsers,
  currentUserId,
  canAuthorShared,
  initialValues,
  onSubmit,
  onDelete,
  onCancel,
}: AutomationFormProps) {
  const defaultValues = useMemo(
    () => initialValues ?? createLightweightAutomationFormValues(tenantId),
    [initialValues, tenantId],
  );
  const defaultValuesKey = useMemo(() => JSON.stringify(defaultValues), [defaultValues]);
  const schema = useMemo(
    () =>
      createLightweightAutomationFormSchema({
        canAuthorShared,
      }),
    [canAuthorShared],
  );
  const form = useForm<LightweightAutomationFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
  });
  const { register, handleSubmit, setValue, watch, reset, formState } = form;
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValuesKey, defaultValues, reset]);

  const values = watch();
  const preview = useMemo(() => buildDeliveryPreview(values), [values]);
  const usesCustomTemplate =
    (values.deliveryMode === 'api_batch' && values.apiBatchBodyShape === 'json_template.v1') ||
    (values.deliveryMode === 'webhook' && values.webhookBodyShape === 'json_template.v1');
  const showsRequestPreview = values.deliveryMode === 'api_batch' || values.deliveryMode === 'webhook';

  const submitHandler = handleSubmit(async (nextValues) => {
    setSubmitErrors([]);
    await onSubmit(toRecord(tenantId, currentUserId, nextValues));
  }, (errors) => {
    setSubmitErrors(flattenErrors(errors));
  });

  const selectedSharedUserIds = values.sharedUserIds ?? [];
  const selectedSharedRoleKeys = values.sharedRoleKeys ?? [];

  return (
    <form onSubmit={submitHandler} className="flex flex-col gap-6">
      {submitErrors.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Fix the validation errors before saving.</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-1 text-sm" style={{ color: 'var(--danger)' }}>
            {submitErrors.map((error) => (
              <span key={error}>{error}</span>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <TextField label="Automation ID" error={formState.errors.automationId?.message} registration={register('automationId')} />
          <TextField label="Name" error={formState.errors.name?.message} registration={register('name')} />
          <SelectField label="Agent" error={formState.errors.agentId?.message} registration={register('agentId')}>
            <option value="">Select agent</option>
            {agents.map((agent) => (
              <option key={agent.agentId} value={agent.agentId}>{agent.name}</option>
            ))}
          </SelectField>
          <SelectField label="Status" error={formState.errors.status?.message} registration={register('status')}>
            {statusOptions.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </SelectField>
          <ToggleField label="Enabled" registration={register('enabled')} />
          <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
            {canAuthorShared ? (
              <SelectField label="Ownership" error={formState.errors.ownerMode?.message} registration={register('ownerMode')}>
                {ownerModeOptions.map((ownerMode) => (
                  <option key={ownerMode} value={ownerMode}>{ownerMode}</option>
                ))}
              </SelectField>
            ) : (
              <div
                className="rounded-sm border border-border p-3 text-xs"
                style={{ background: 'var(--bg-elevated)', color: 'var(--muted-foreground)' }}
              >
                This automation is self-owned and can only notify the current user.
              </div>
            )}
            <div
              className="rounded-[var(--radius-sm)] border border-border p-3 text-xs"
              style={{ background: 'var(--bg-elevated)', color: 'var(--muted-foreground)' }}
            >
              {values.ownerMode === 'shared'
                ? 'Shared automations are operator-authored and deliver inbox results to the selected tenant users and roles.'
                : 'Self-owned automations always resolve their inbox audience to the current authenticated user.'}
            </div>
          </div>
          {values.ownerMode === 'shared' ? (
            <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium" style={{ color: 'var(--text-strong)' }}>Shared Users</span>
                <div className="flex flex-col gap-2">
                  {tenantUsers.map((user) => {
                    const checked = selectedSharedUserIds.includes(user.id);
                    return (
                      <label
                        key={user.id}
                        className="flex items-start gap-3 rounded-md border border-border px-3 py-2 cursor-pointer"
                        style={{ background: checked ? 'var(--bg-accent)' : 'var(--bg-elevated)' }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const nextValue = checked
                              ? selectedSharedUserIds.filter((value) => value !== user.id)
                              : [...selectedSharedUserIds, user.id].toSorted();
                            setValue('sharedUserIds', nextValue, { shouldDirty: true, shouldValidate: true });
                          }}
                        />
                        <span className="flex flex-col gap-0.5">
                          <span className="text-sm font-medium" style={{ color: 'var(--text-strong)' }}>
                            {user.displayName || user.email || user.id}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                            {user.email || user.id}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {formState.errors.sharedUserIds?.message ? (
                  <span className="text-xs" style={{ color: 'var(--danger)' }}>{formState.errors.sharedUserIds.message}</span>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium" style={{ color: 'var(--text-strong)' }}>Shared Roles</span>
                <TenantAppUserRolePicker
                  value={selectedSharedRoleKeys}
                  onChange={(nextValue) => {
                    setValue('sharedRoleKeys', nextValue, { shouldDirty: true, shouldValidate: true });
                  }}
                />
              </div>
            </div>
          ) : null}
          <div className="md:col-span-2">
            <TextAreaField
              label="Instructions"
              hint="Describe the task, scope, and expected output in plain language. The runtime infers the bounded capability profile internally."
              error={formState.errors.instructionsMarkdown?.message}
              registration={register('instructionsMarkdown')}
              rows={12}
            />
          </div>
          <input type="hidden" {...register('moduleKey')} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Output</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <SelectField label="Delivery Mode" error={formState.errors.deliveryMode?.message} registration={register('deliveryMode')}>
            {outputModeOptions.map((mode) => (
              <option key={mode} value={mode}>{mode}</option>
            ))}
          </SelectField>
          {values.deliveryMode === 'message' ? (
            <>
              <SelectField label="Message Destination" error={formState.errors.digestDestination?.message} registration={register('digestDestination')}>
                {digestDestinationOptions.map((destination) => (
                  <option key={destination} value={destination}>{destination}</option>
                ))}
              </SelectField>
              {values.digestDestination !== 'inbox' ? (
                <TextField label="Message Target Ref" error={formState.errors.digestTargetRef?.message} registration={register('digestTargetRef')} />
              ) : null}
            </>
          ) : null}
          {values.deliveryMode === 'api_batch' ? (
            <>
              <TextField label="API Batch URL" error={formState.errors.apiBatchUrl?.message} registration={register('apiBatchUrl')} />
              <TextField label="Auth Token Secret Ref" error={formState.errors.apiBatchSecretRef?.message} registration={register('apiBatchSecretRef')} />
              <SelectField label="Request Body Shape" error={formState.errors.apiBatchBodyShape?.message} registration={register('apiBatchBodyShape')}>
                {apiBatchBodyShapeOptions.map((shape) => (
                  <option key={shape} value={shape}>{shape}</option>
                ))}
              </SelectField>
              <TextField label="Timeout (ms)" error={formState.errors.apiBatchTimeoutMs?.message} registration={register('apiBatchTimeoutMs')} />
              <div className="md:col-span-2">
                <TextAreaField label="Headers JSON" error={formState.errors.apiBatchHeaders?.message} registration={register('apiBatchHeaders')} rows={5} />
              </div>
              {values.apiBatchBodyShape === 'json_template.v1' ? (
                <div className="md:col-span-2">
                  <TextAreaField label="Request Body Template JSON" error={formState.errors.apiBatchBodyTemplate?.message} registration={register('apiBatchBodyTemplate')} rows={8} />
                </div>
              ) : null}
            </>
          ) : null}
          {values.deliveryMode === 'webhook' ? (
            <>
              <TextField label="Webhook URL" error={formState.errors.webhookUrl?.message} registration={register('webhookUrl')} />
              <TextField label="Auth Token Secret Ref" error={formState.errors.webhookSecretRef?.message} registration={register('webhookSecretRef')} />
              <SelectField label="Request Body Shape" error={formState.errors.webhookBodyShape?.message} registration={register('webhookBodyShape')}>
                {webhookBodyShapeOptions.map((shape) => (
                  <option key={shape} value={shape}>{shape}</option>
                ))}
              </SelectField>
              <TextField label="Timeout (ms)" error={formState.errors.webhookTimeoutMs?.message} registration={register('webhookTimeoutMs')} />
              <div className="md:col-span-2">
                <TextAreaField label="Headers JSON" error={formState.errors.webhookHeaders?.message} registration={register('webhookHeaders')} rows={5} />
              </div>
              {values.webhookBodyShape === 'json_template.v1' ? (
                <div className="md:col-span-2">
                  <TextAreaField label="Request Body Template JSON" error={formState.errors.webhookBodyTemplate?.message} registration={register('webhookBodyTemplate')} rows={8} />
                </div>
              ) : null}
            </>
          ) : null}
          {usesCustomTemplate ? (
            <div className="md:col-span-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Template variables: {LIGHTWEIGHT_AUTOMATION_TEMPLATE_VARIABLES.join(', ')}
            </div>
          ) : null}
          {showsRequestPreview ? (
            <div className="md:col-span-2">
              <JsonPreview title="Request Body Preview" value={preview} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Input and Run Settings</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <SelectField label="Input Source" error={formState.errors.inputSourceKind?.message} registration={register('inputSourceKind')}>
            {inputSourceKindOptions.map((kind) => (
              <option key={kind} value={kind}>{kind}</option>
            ))}
          </SelectField>
          <SelectField label="Iteration Policy" error={formState.errors.iterationKind?.message} registration={register('iterationKind')}>
            {iterationKindOptions.map((kind) => (
              <option key={kind} value={kind}>{kind}</option>
            ))}
          </SelectField>
          {values.inputSourceKind === 'static_list' ? (
            <div className="md:col-span-2">
              <TextAreaField
                label="Static List Items JSON"
                hint="Provide a JSON array of values or objects. The runtime will iterate these items; the model should reason per item."
                error={formState.errors.staticListItemsJson?.message}
                registration={register('staticListItemsJson')}
                rows={8}
              />
            </div>
          ) : null}
          {values.inputSourceKind === 'http' ? (
            <>
              <TextField
                label="HTTP URL"
                hint="The runtime fetches this JSON endpoint and resolves iterable items from the configured path."
                error={formState.errors.httpUrl?.message}
                registration={register('httpUrl')}
              />
              <SelectField label="HTTP Method" error={formState.errors.httpMethod?.message} registration={register('httpMethod')}>
                {httpMethodOptions.map((method) => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </SelectField>
              <TextField label="Items Path" error={formState.errors.httpItemsPath?.message} registration={register('httpItemsPath')} />
              <TextField label="HTTP Limit" error={formState.errors.httpLimit?.message} registration={register('httpLimit')} />
              <div className="md:col-span-2">
                <TextAreaField
                  label="HTTP Headers JSON"
                  hint="Optional JSON object of request headers."
                  error={formState.errors.httpHeadersJson?.message}
                  registration={register('httpHeadersJson')}
                  rows={6}
                />
              </div>
              {values.httpMethod === 'POST' ? (
                <div className="md:col-span-2">
                  <TextAreaField
                    label="HTTP Body JSON"
                    hint="Optional JSON body for POST requests."
                    error={formState.errors.httpBodyJson?.message}
                    registration={register('httpBodyJson')}
                    rows={6}
                  />
                </div>
              ) : null}
            </>
          ) : null}
          {values.inputSourceKind === 'webhook' ? (
            <>
              <TextField
                label="Items Path"
                hint="Dot path inside the incoming webhook payload that resolves to the iterable items array."
                error={formState.errors.webhookItemsPath?.message}
                registration={register('webhookItemsPath')}
              />
              <TextField
                label="Webhook Limit"
                error={formState.errors.webhookLimit?.message}
                registration={register('webhookLimit')}
              />
            </>
          ) : null}
          {values.inputSourceKind === 'connector' ? (
            <>
              <TextField
                label="Connector Tool Name"
                hint="Use the exact managed connector read tool name, for example crm.leads.list."
                error={formState.errors.connectorToolName?.message}
                registration={register('connectorToolName')}
              />
              <TextField
                label="Items Path"
                hint="Dot path inside the connector output that resolves to the iterable items array."
                error={formState.errors.connectorItemsPath?.message}
                registration={register('connectorItemsPath')}
              />
              <TextField
                label="Connector Limit"
                error={formState.errors.connectorLimit?.message}
                registration={register('connectorLimit')}
              />
              <div className="md:col-span-2">
                <TextAreaField
                  label="Connector Input JSON"
                  hint="Provide the exact connector read input payload as a JSON object."
                  error={formState.errors.connectorInputJson?.message}
                  registration={register('connectorInputJson')}
                  rows={6}
                />
              </div>
            </>
          ) : null}
          {values.iterationKind === 'for_each' ? (
            <>
              <TextField label="Max Items Per Run" error={formState.errors.maxItemsPerRun?.message} registration={register('maxItemsPerRun')} />
              <TextField label="Concurrency" error={formState.errors.concurrency?.message} registration={register('concurrency')} />
              <ToggleField label="Continue On Error" registration={register('continueOnError')} />
              <ToggleField label="Aggregate Results" registration={register('aggregateResults')} />
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <SelectField label="Execution Mode" error={formState.errors.executionMode?.message} registration={register('executionMode')}>
            {executionModeOptions.map((mode) => (
              <option key={mode} value={mode}>{mode}</option>
            ))}
          </SelectField>
          <SelectField label="Execution Class" error={formState.errors.executionClass?.message} registration={register('executionClass')}>
            {executionClassOptions.map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </SelectField>
          {values.executionMode === 'scheduled' ? (
            <>
              <SelectField label="Schedule Kind" error={formState.errors.scheduleKind?.message} registration={register('scheduleKind')}>
                {scheduleKindOptions.map((kind) => (
                  <option key={kind} value={kind}>{kind}</option>
                ))}
              </SelectField>
              <TextField label="Timezone" error={formState.errors.scheduleTz?.message} registration={register('scheduleTz')} />
              {values.scheduleKind === 'cron' ? (
                <TextField label="Cron" error={formState.errors.scheduleCron?.message} registration={register('scheduleCron')} />
              ) : null}
              {values.scheduleKind === 'every' ? (
                <TextField label="Every (ms)" error={formState.errors.scheduleEveryMs?.message} registration={register('scheduleEveryMs')} />
              ) : null}
              {values.scheduleKind === 'at' ? (
                <TextField label="Run At" error={formState.errors.scheduleAt?.message} registration={register('scheduleAt')} />
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button type="submit" onClick={() => void submitHandler()} disabled={formState.isSubmitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        {onDelete ? (
          <Button type="button" variant="destructive" onClick={() => void onDelete()}>
            Delete
          </Button>
        ) : null}
      </div>
    </form>
  );
}
